import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  useDeleteDocument,
  useDocumentDownloadUrl,
  useListDocuments,
  useUpdateDocumentTags,
  useUploadDocument,
} from '@/hooks/useDocuments';
import {
  Camera,
  Search,
  Star,
  StarOff,
  Upload as UploadIcon,
  X,
} from 'lucide-react';
import type React from 'react';
import { useRef, useState } from 'react';

interface AssetPhotosProps {
  assetId: number;
}

// Separate component for photo thumbnail with proper URL handling
const PhotoThumbnail: React.FC<{
  photo: any;
  onView: (photo: any) => void;
  onDelete: (id: number) => void;
  isDeleting: boolean;
  isCover: boolean;
  onSetCover: (photo: any) => void;
  isSettingCover: boolean;
}> = ({
  photo,
  onView,
  onDelete,
  isDeleting,
  isCover,
  onSetCover,
  isSettingCover,
}) => {
  const { data: thumbnailUrl, isLoading: thumbnailLoading } =
    useDocumentDownloadUrl(photo.id, { enabled: !!photo.id });

  return (
    <div className="group relative aspect-square bg-muted rounded-lg overflow-hidden border hover:shadow-md transition-shadow">
      {thumbnailLoading ? (
        <div className="w-full h-full flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
        </div>
      ) : thumbnailUrl ? (
        <img
          src={thumbnailUrl}
          alt={photo.original_name}
          className="w-full h-full object-cover cursor-pointer"
          onClick={() => onView(photo)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onView(photo);
            }
          }}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-muted-foreground">
          <Camera className="h-8 w-8" />
        </div>
      )}
      {/* Cover badge */}
      {isCover && (
        <div className="absolute top-2 left-2 bg-yellow-400 text-white rounded-full px-2 py-1 text-xs flex items-center gap-1 shadow">
          <Star className="h-3 w-3" /> Cover
        </div>
      )}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
          <Button size="sm" variant="secondary" onClick={() => onView(photo)}>
            View
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onSetCover(photo)}
            disabled={isCover || isSettingCover}
          >
            {isCover ? (
              <Star className="h-4 w-4 text-yellow-400" />
            ) : (
              <StarOff className="h-4 w-4" />
            )}
            {isCover ? 'Cover' : isSettingCover ? 'Setting...' : 'Set as Cover'}
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={() => onDelete(photo.id)}
            disabled={isDeleting}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
        <p className="text-white text-sm truncate">{photo.original_name}</p>
      </div>
    </div>
  );
};

export const AssetPhotos: React.FC<AssetPhotosProps> = ({ assetId }) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [search, setSearch] = useState<string>('');
  const [viewPhoto, setViewPhoto] = useState<any | null>(null);
  const [settingCoverId, setSettingCoverId] = useState<number | null>(null);
  const {
    data: photos,
    isLoading,
    error,
    refetch,
  } = useListDocuments({ asset_id: assetId, search, tags: ['photo'] });
  const uploadMutation = useUploadDocument();
  const deleteMutation = useDeleteDocument();
  const updateTagsMutation = useUpdateDocumentTags();
  const { data: viewUrl, isLoading: viewLoading } = useDocumentDownloadUrl(
    viewPhoto?.id,
    { enabled: !!viewPhoto },
  );

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file (jpg, png, gif, webp)');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('asset_id', String(assetId));
    formData.append('tags', 'photo'); // Tag as photo
    uploadMutation.mutate(formData);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Delete this photo?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleView = (photo: any) => {
    setViewPhoto(photo);
  };

  const handleCloseView = () => {
    setViewPhoto(null);
  };

  const isImageFile = (fileName: string): boolean => {
    return /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);
  };

  const filteredPhotos =
    photos?.filter((photo) => isImageFile(photo.original_name)) || [];
  const coverPhotoId = filteredPhotos.find((p) =>
    (p.tags || []).includes('cover'),
  )?.id;

  // Set as cover: remove 'cover' from all, add to selected
  const handleSetCover = async (photo: any) => {
    setSettingCoverId(photo.id);
    try {
      // Remove 'cover' from all other photos
      await Promise.all(
        filteredPhotos.map((p) => {
          if (p.id === photo.id) return Promise.resolve();
          if ((p.tags || []).includes('cover')) {
            const newTags = (p.tags || []).filter((t: string) => t !== 'cover');
            return updateTagsMutation.mutateAsync({ id: p.id, tags: newTags });
          }
          return Promise.resolve();
        }),
      );
      // Add 'cover' to selected photo
      const newTags = Array.from(new Set([...(photo.tags || []), 'cover']));
      await updateTagsMutation.mutateAsync({ id: photo.id, tags: newTags });
      refetch?.();
    } catch (err) {
      alert('Failed to set cover photo.');
    } finally {
      setSettingCoverId(null);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>Photos</CardTitle>
        <div className="flex items-center gap-2">
          <div className="relative w-60">
            <Input
              type="text"
              placeholder="Search photos..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
              style={{ minWidth: 220 }}
            />
            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          </div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="hidden"
            id="asset-photo-upload"
            accept="image/*"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadMutation.isPending}
            size="sm"
            variant="outline"
          >
            <Camera className="h-4 w-4 mr-1" />
            Add Photo
          </Button>
          {uploadMutation.isPending && <span>Uploading...</span>}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        )}

        {error && (
          <div className="text-center py-8 text-red-500">
            Error loading photos. Please try again.
          </div>
        )}

        {!isLoading && !error && filteredPhotos.length === 0 && (
          <div className="text-center py-12">
            <Camera className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No photos yet</h3>
            <p className="text-muted-foreground mb-4">
              Add photos of your property to keep a visual record.
            </p>
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMutation.isPending}
            >
              <Camera className="h-4 w-4 mr-2" />
              Add Your First Photo
            </Button>
          </div>
        )}

        {!isLoading && !error && filteredPhotos.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredPhotos.map((photo) => (
              <PhotoThumbnail
                key={photo.id}
                photo={photo}
                onView={handleView}
                onDelete={handleDelete}
                isDeleting={deleteMutation.isPending}
                isCover={coverPhotoId === photo.id}
                onSetCover={handleSetCover}
                isSettingCover={settingCoverId === photo.id}
              />
            ))}
          </div>
        )}
      </CardContent>

      {/* View Photo Modal */}
      <Dialog open={!!viewPhoto} onOpenChange={handleCloseView}>
        <DialogContent className="max-w-4xl w-full h-[80vh]">
          <DialogHeader>
            <DialogTitle>
              {viewPhoto?.original_name || 'View Photo'}
            </DialogTitle>
          </DialogHeader>
          {viewLoading ? (
            <div className="flex justify-center items-center h-64">
              Loading...
            </div>
          ) : viewUrl ? (
            <div className="w-full h-full flex flex-col items-center justify-center">
              <img
                src={viewUrl}
                alt={viewPhoto?.original_name}
                className="max-h-[70vh] max-w-full object-contain border rounded"
              />
            </div>
          ) : (
            <div className="text-center text-muted-foreground">
              No preview available.
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};
